import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { EllipsisVertical, PenIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { formatCurrencyValue } from '../../utils';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import NumberField from '@/components/NumberField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

const GET_DEAL_OPPORTUNITY_CAPTABLE = gql`
  query DealOpportunity_CapTable($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        ltmEbitda
        referencePoint
        computedSecuritySummary
        computedSourcesUses
        computedProjectedDebtIrr
        computedProjectedDebtMoic
        computedProjectedEquityIrr
        computedProjectedEquityMoic
        computedProjectedTotalIrr
        computedProjectedTotalMoic
        projectedDebtIrr
        projectedDebtMoic
        projectedEquityIrr
        projectedEquityMoic
        projectedTotalIrr
        projectedTotalMoic
      }
    }
  }
`;

interface GetOpportunityCashUse {
  opportunityCashUses: {
    __typename: 'OpportunityCashUseNode';
    amount: null | number;
    cashNoncash: null | string;
    cashUse: null | string;
    id: string;
  }[];
}

type OpportunityCashUse = GetOpportunityCashUse['opportunityCashUses'][number];

const GET_OPPORTUNITY_CASH_USE = gql`
  query OpportunityCashUse($deal_id: ID!) {
    opportunityCashUses(dealOpportunityId: $deal_id) {
      id
      cashUse
      amount
      cashNoncash
    }
  }
`;

const CREATE_CASH_USE = gql`
  mutation CreateCashUse(
    $deal_opportunity_id: ID!
    $cash_use: String
    $amount: Float
    $cash_noncash: String
  ) {
    createOpportunityCashUse(
      dealOpportunityId: $deal_opportunity_id
      values: {
        cashUse: $cash_use
        amount: $amount
        cashNoncash: $cash_noncash
      }
    ) {
      ok
    }
  }
`;

const UPDATE_CASH_USE = gql`
  mutation UpdateCashUse(
    $id: ID!
    $cash_use: String
    $amount: Float
    $cash_noncash: String
  ) {
    updateOpportunityCashUse(
      id: $id
      values: {
        cashUse: $cash_use
        amount: $amount
        cashNoncash: $cash_noncash
      }
    ) {
      ok
    }
  }
`;

const DELETE_SECURITY_CASH_USE = gql`
  mutation DeleteSecurityCashUse($use_id: ID!) {
    deleteOpportunityCashUse(id: $use_id) {
      ok
    }
  }
`;

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{
  title: string;
}>) => (
  <div className="mb-2 flex h-9 items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const formatCashNoncashLabel = (value: null | string | undefined): string => {
  if (!value) return '-';
  if (value === 'non_cash') return 'Non-Cash';
  if (value === 'cash') return 'Cash';
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export interface CashUseSectionProps {
  dealOpportunityId: string;
  onCashUseTotalChange?: (total: null | number) => void;
}

export const CashUseSection = ({
  dealOpportunityId,
  onCashUseTotalChange
}: CashUseSectionProps) => {
  const [cashUseDialogIsOpen, setCashUseDialogIsOpen] = useState(false);
  const [cashUseDialogKind, setCashUseDialogKind] = useState<
    'cash' | 'non_cash'
  >('cash');
  const [cashUseEditDialogIsOpen, setCashUseEditDialogIsOpen] = useState(false);
  const [cashUseBeingEdited, setCashUseBeingEdited] =
    useState<null | OpportunityCashUse>(null);
  const [cashUseDeleteDialogIsOpen, setCashUseDeleteDialogIsOpen] =
    useState(false);
  const [cashUseBeingDeleted, setCashUseBeingDeleted] =
    useState<null | OpportunityCashUse>(null);

  const { data, loading } = useQuery<
    GetOpportunityCashUse,
    { deal_id: string }
  >(GET_OPPORTUNITY_CASH_USE, {
    variables: { deal_id: dealOpportunityId }
  });

  const [createCashUse, { loading: isCreatingCashUse }] =
    useMutation(CREATE_CASH_USE);
  const [updateCashUse, { loading: isUpdatingCashUse }] =
    useMutation(UPDATE_CASH_USE);

  const [deleteSecurityCashUse, { loading: isDeletingSecurityCashUse }] =
    useMutation(DELETE_SECURITY_CASH_USE);

  useEffect(() => {
    if (onCashUseTotalChange) {
      if (!data) {
        onCashUseTotalChange(null);
      } else {
        const total = data.opportunityCashUses.reduce(
          (sum, c) => sum + (c.amount ?? 0),
          0
        );
        onCashUseTotalChange(total);
      }
    }
  }, [data, onCashUseTotalChange]);

  useEffect(() => {
    if (!cashUseEditDialogIsOpen) {
      setCashUseBeingEdited(null);
    }
  }, [cashUseEditDialogIsOpen]);

  useEffect(() => {
    if (!cashUseDeleteDialogIsOpen) {
      setCashUseBeingDeleted(null);
    }
  }, [cashUseDeleteDialogIsOpen]);

  const handleCashUseFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const cashUse = formData.get('uses-field');
    const amount = formData.get('amount-field');
    const cashNoncash = formData.get('cash-noncash-field');
    const amountValue = typeof amount === 'string' ? amount : '';

    try {
      const { error } = await createCashUse({
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY_CAPTABLE,
            variables: { id: dealOpportunityId }
          },
          {
            query: GET_OPPORTUNITY_CASH_USE,
            variables: { deal_id: dealOpportunityId }
          }
        ],
        variables: {
          amount: amountValue ? +amountValue : null,
          cash_noncash: cashNoncash === '' ? null : cashNoncash,
          cash_use: cashUse === '' ? null : cashUse,
          deal_opportunity_id: dealOpportunityId
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setCashUseDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleEditCashUseFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!cashUseBeingEdited) return;

    const formData = new FormData(event.currentTarget);

    const cashUse = formData.get('edit-uses-field');
    const amount = formData.get('edit-amount-field');
    const cashNoncash = formData.get('edit-cash-noncash-field');
    const amountValue = typeof amount === 'string' ? amount : '';

    try {
      const { error } = await updateCashUse({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY_CAPTABLE,
            variables: { id: dealOpportunityId }
          },
          {
            query: GET_OPPORTUNITY_CASH_USE,
            variables: { deal_id: dealOpportunityId }
          }
        ],
        variables: {
          amount: amountValue ? +amountValue.replace(/,/g, '') : null,
          cash_noncash: cashNoncash === '' ? null : cashNoncash,
          cash_use: cashUse === '' ? null : cashUse,
          id: cashUseBeingEdited.id
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setCashUseEditDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleDeleteCashUse = async (cashUseId: string) => {
    try {
      const { error } = await deleteSecurityCashUse({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY_CAPTABLE,
            variables: { id: dealOpportunityId }
          },
          {
            query: GET_OPPORTUNITY_CASH_USE,
            variables: { deal_id: dealOpportunityId }
          }
        ],
        variables: { use_id: cashUseId }
      });

      if (error) {
        throw new Error('An error has occurred');
      }
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleConfirmDeleteCashUse = async () => {
    if (!cashUseBeingDeleted) {
      return;
    }

    await handleDeleteCashUse(cashUseBeingDeleted.id);
    setCashUseDeleteDialogIsOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <SectionHeader title="Cash Use" />
        <div className="bg-card border-border flex min-h-[120px] items-center justify-center rounded-md border p-4">
          <Spinner className="size-6" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-2">
        <SectionHeader title="Cash Use" />
        <div className="bg-card border-border text-muted-foreground rounded-md border p-4 text-sm">
          Error loading cash use.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SectionHeader title="Cash Use">
        <Dialog
          onOpenChange={open => {
            setCashUseDialogIsOpen(open);
            if (open) setCashUseDialogKind('cash');
          }}
          open={cashUseDialogIsOpen}
        >
          {data.opportunityCashUses.length !== 0 && (
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setCashUseDialogKind('cash');
                  setCashUseDialogIsOpen(true);
                }}
              >
                <PlusIcon />
                Add Row
              </Button>
            </DialogTrigger>
          )}
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                {cashUseDialogKind === 'non_cash' ? 'Non Cash Use' : 'Cash Use'}
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id="cash-use-form"
                noValidate
                onSubmit={event => void handleCashUseFormSubmit(event)}
              >
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-x-2.5">
                    <Field>
                      <FieldLabel htmlFor="uses-field">Uses</FieldLabel>
                      <Input
                        id="uses-field"
                        name="uses-field"
                        placeholder="Enter Value"
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="amount-field">Amount</FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          id="amount-field"
                          name="amount-field"
                          placeholder="Enter Value"
                        />
                      </InputGroup>
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="cash-noncash-field">
                      Cash / Non-Cash
                    </FieldLabel>
                    <Select
                      defaultValue={cashUseDialogKind}
                      name="cash-noncash-field"
                    >
                      <SelectTrigger
                        className="w-full"
                        id="cash-noncash-field"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="non_cash">Non-Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isCreatingCashUse}
                form="cash-use-form"
                type="submit"
              >
                {isCreatingCashUse && <Spinner />}
                Add Row
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SectionHeader>

      <Dialog
        onOpenChange={setCashUseEditDialogIsOpen}
        open={cashUseEditDialogIsOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Edit Cash Use
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            {cashUseBeingEdited && (
              <form
                id="edit-cash-use-form"
                noValidate
                onSubmit={event => void handleEditCashUseFormSubmit(event)}
              >
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-x-2.5">
                    <Field>
                      <FieldLabel htmlFor="edit-uses-field">Uses</FieldLabel>
                      <Input
                        defaultValue={cashUseBeingEdited.cashUse ?? ''}
                        id="edit-uses-field"
                        name="edit-uses-field"
                        placeholder="Enter Value"
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="edit-amount-field">
                        Amount
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          defaultValue={cashUseBeingEdited.amount ?? undefined}
                          id="edit-amount-field"
                          name="edit-amount-field"
                          placeholder="Enter Value"
                        />
                      </InputGroup>
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="edit-cash-noncash-field">
                      Cash / Non-Cash
                    </FieldLabel>
                    <Select
                      defaultValue={cashUseBeingEdited.cashNoncash ?? 'cash'}
                      name="edit-cash-noncash-field"
                    >
                      <SelectTrigger
                        className="w-full"
                        id="edit-cash-noncash-field"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="non_cash">Non-Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </FieldGroup>
              </form>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              disabled={isUpdatingCashUse}
              form="edit-cash-use-form"
              type="submit"
            >
              {isUpdatingCashUse && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        onOpenChange={setCashUseDeleteDialogIsOpen}
        open={cashUseDeleteDialogIsOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete cash use row?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-medium">
                {cashUseBeingDeleted?.cashUse ?? 'this row'}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSecurityCashUse}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!cashUseBeingDeleted || isDeletingSecurityCashUse}
              onClick={() => void handleConfirmDeleteCashUse()}
              variant="destructive"
            >
              {isDeletingSecurityCashUse && <Spinner />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="bg-card border-border rounded-md border p-4">
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-semibold">Cash Use</TableHead>
              <TableHead className="text-xs font-semibold">
                Cash/Non-Cash
              </TableHead>
              <TableHead className="text-right text-xs font-semibold">
                Amount
              </TableHead>
              <TableHead className="text-right text-xs font-semibold" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.opportunityCashUses.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  className="p-0"
                  colSpan={4}
                >
                  <Empty className="px-0! pt-4! pb-0!">
                    <EmptyContent>
                      <Button
                        onClick={() => {
                          setCashUseDialogKind('cash');
                          setCashUseDialogIsOpen(true);
                        }}
                      >
                        <PlusIcon />
                        Add Row
                      </Button>
                    </EmptyContent>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              data.opportunityCashUses.map(opportunityCashUse => (
                <TableRow key={opportunityCashUse.id}>
                  <TableCell>{opportunityCashUse.cashUse}</TableCell>
                  <TableCell>
                    {formatCashNoncashLabel(opportunityCashUse.cashNoncash)}
                  </TableCell>
                  <TableCell className="text-right">
                    {opportunityCashUse.amount
                      ? formatCurrencyValue(opportunityCashUse.amount)
                      : ''}
                  </TableCell>
                  <TableCell className="w-0">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="size-8"
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            size="icon"
                            type="button"
                            variant="ghost"
                          >
                            <EllipsisVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setCashUseBeingEdited(opportunityCashUse);
                              setCashUseEditDialogIsOpen(true);
                            }}
                          >
                            <PenIcon />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={isDeletingSecurityCashUse}
                            onSelect={() => {
                              setCashUseBeingDeleted(opportunityCashUse);
                              setCashUseDeleteDialogIsOpen(true);
                            }}
                            variant="destructive"
                          >
                            <Trash2Icon />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
